import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { certificateService } from "@/services/certificate/certificate.service";
import type { Certificate, CertificateGenerationLog } from "@/types/certificate.types";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Download,
  Calendar,
  CheckCircle2,
  XCircle,
  Shield,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Clock,
  Send,
} from "lucide-react";
import { format } from "date-fns";
import { PdfPreview } from "@/components/form/form-answer/previews/pdf-preview";
import { Card, CardContent } from "../ui";

interface CertificateDisplayProps {
  applicantProcessId: string;
  isAdmin?: boolean;
  canRelease?: boolean;
}

const CertificateDisplay: React.FC<CertificateDisplayProps> = ({
  applicantProcessId,
  isAdmin = false,
  canRelease = false,
}) => {
  const { t } = useTranslation();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [failedLogs, setFailedLogs] = useState<CertificateGenerationLog[]>([]);
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [loadingPreview, setLoadingPreview] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    loadData();
  }, [applicantProcessId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [certs, logs, eligibility] = await Promise.all([
        certificateService.getCertificatesByApplicantProcess(applicantProcessId),
        certificateService.getGenerationLogsByApplicantProcess(applicantProcessId),
        certificateService.checkEligibility(applicantProcessId),
      ]);
      setCertificates(certs);
      setFailedLogs(logs.filter((l) => l.status === 'FAILED'));
      setEligible(eligibility?.eligible ?? false);

      const urlMap: Record<string, string> = {};
      const loadingMap: Record<string, boolean> = {};

      for (const cert of certs) {
        loadingMap[cert.id] = true;
        try {
          const { downloadUrl } =
            await certificateService.getCertificateDownloadUrl(cert.id);
          urlMap[cert.id] = downloadUrl;
        } catch (error: any) {
          console.error(
            `Error loading preview URL for certificate ${cert.id}:`,
            error
          );
        } finally {
          loadingMap[cert.id] = false;
        }
      }

      setPreviewUrls(urlMap);
      setLoadingPreview(loadingMap);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error("Error loading certificates:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async (certificateId: string) => {
    try {
      setReleasing(certificateId);
      await certificateService.releaseCertificate(certificateId);
      toast.success(t('certificate.certificateDisplay.releaseSuccess'));
      await loadData();
    } catch (error: any) {
      toast.error(t('certificate.certificateDisplay.releaseFailed'));
      console.error(error);
    } finally {
      setReleasing(null);
    }
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      const result = await certificateService.retryGeneration(applicantProcessId);
      if (result.generated) {
        toast.success(t('certificate.certificateDisplay.retrySuccess'));
        await loadData();
      } else {
        toast.error(t('certificate.certificateDisplay.retryConditionNotMet'));
      }
    } catch (error: any) {
      toast.error(t('certificate.certificateDisplay.retryFailed'));
      console.error(error);
    } finally {
      setRetrying(false);
    }
  };

  const handleDownload = async (certificateId: string) => {
    try {
      setDownloading(certificateId);
      const { downloadUrl } =
        await certificateService.getCertificateDownloadUrl(certificateId);
      window.open(downloadUrl, "_blank");
    } catch (error: any) {
      toast.error(
        t("certificate.certificateDisplay.failedToDownloadCertificate")
      );
      console.error(error);
    } finally {
      setDownloading(null);
    }
  };

  const getStatusIcon = (certificate: Certificate) => {
    if (certificate.expiryDate === null) {
      return <Shield className="h-4 w-4 text-gray-600" />;
    }
    if (certificate.isExpired) {
      return <XCircle className="h-4 w-4 text-gray-500" />;
    }
    return <CheckCircle2 className="h-4 w-4 text-gray-600" />;
  };

  const getStatusText = (certificate: Certificate) => {
    if (certificate.expiryDate === null) {
      return t("certificate.certificateDisplay.foreverValid");
    }
    if (certificate.isExpired) {
      return t("certificate.certificateDisplay.expired");
    }
    return t("certificate.certificateDisplay.valid");
  };

  const getStatusBadgeClass = (certificate: Certificate) => {
    if (certificate.expiryDate === null) {
      return "bg-gray-100 text-gray-700";
    }
    if (certificate.isExpired) {
      return "bg-gray-100 text-gray-500";
    }
    return "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="mt-6 bg-white rounded-lg p-6 text-center text-gray-500">
        {t("certificate.certificateDisplay.loadingCertificates")}
      </div>
    );
  }

  const issuedCerts = certificates.filter((c) => c.issuedAt !== null);
  const pendingCerts = certificates.filter((c) => c.issuedAt === null);
  const visibleCerts = canRelease ? certificates : issuedCerts;

  if (visibleCerts.length === 0 && failedLogs.length === 0 && !eligible) {
    return null;
  }

  return (
    <>
      {eligible && visibleCerts.length === 0 && failedLogs.length === 0 && (
        <div className="mt-6">
          <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-blue-800">
                  {t('certificate.certificateDisplay.eligibleNoCertificate')}
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                disabled={retrying}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
                {retrying ? t('certificate.certificateDisplay.retrying') : t('certificate.certificateDisplay.generate')}
              </Button>
            </div>
            <p className="text-xs text-blue-700">
              {t('certificate.certificateDisplay.eligibleNoCertificateDetail')}
            </p>
          </div>
        </div>
      )}

      {failedLogs.length > 0 && (() => {
        const latest = failedLogs[0];
        return (
          <div className="mt-6">
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <h3 className="text-sm font-semibold text-red-800">
                    {t('certificate.certificateDisplay.generationFailed')}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  disabled={retrying}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
                  {retrying ? t('certificate.certificateDisplay.retrying') : t('certificate.certificateDisplay.retry')}
                </Button>
              </div>
              <p className="text-xs text-red-700">
                {latest.errorMessage || t('certificate.certificateDisplay.unknownError')}
              </p>
              <p className="text-xs text-red-500">
                {t('certificate.certificateDisplay.lastAttempt')}: {format(new Date(latest.attemptedAt), 'yyyy-MM-dd HH:mm')}
                {latest.retryCount > 0 && ` · ${latest.retryCount} ${t('certificate.certificateDisplay.retries')}`}
              </p>
            </div>
          </div>
        );
      })()}

      {canRelease && pendingCerts.length > 0 && (
        <div className="mt-6 space-y-3">
          {pendingCerts.map((cert) => (
            <div key={cert.id} className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-yellow-800">
                      {cert.certificateTemplate?.name || t('certificate.certificateDisplay.certificate')}
                    </h3>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      {cert.certificateTemplate?.issuanceType === 'RELEASE_DATE' && cert.certificateTemplate.releaseDate
                        ? `${t('certificate.certificateDisplay.scheduledRelease')}: ${format(new Date(cert.certificateTemplate.releaseDate), 'yyyy-MM-dd HH:mm')}`
                        : t('certificate.certificateDisplay.pendingManualRelease')}
                    </p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={releasing === cert.id}
                      className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
                    >
                      <Send className={`h-4 w-4 mr-2 ${releasing === cert.id ? 'animate-pulse' : ''}`} />
                      {releasing === cert.id
                        ? t('certificate.certificateDisplay.releasing')
                        : t('certificate.certificateDisplay.release')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('certificate.certificateDisplay.releaseConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('certificate.certificateDisplay.releaseConfirmDescription')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('certificate.certificateDisplay.releaseConfirmCancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleRelease(cert.id)}>
                        <Send className="h-4 w-4 mr-2" />
                        {t('certificate.certificateDisplay.release')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {visibleCerts.length > 0 && (
      <div className="mt-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("certificate.certificateDisplay.title")}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t("certificate.certificateDisplay.description")}
          </p>
        </div>
        <div className="space-y-4">
          {issuedCerts.map((certificate) => (
            <div key={certificate.id} className="space-y-4">
              <Card className="border-none shadow-none">
                <CardContent className="flex items-start justify-between bg-subprimary/50">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusIcon(certificate)}
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          {certificate.certificateTemplate?.name ||
                            t("certificate.certificateDisplay.certificate")}
                        </h3>
                        <p className="text-sm text-gray-600 mt-0.5">
                          {t(
                            "certificate.certificateDisplay.certificateNumber"
                          )}
                          : {certificate.certificateNumber}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4">
                      <div className="flex items-start gap-3">
                        <Calendar className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            {t("certificate.certificateDisplay.issueDate")}
                          </div>
                          <div className="text-sm text-gray-900">
                            {format(
                              new Date(certificate.issueDate),
                              "yyyy-MM-dd"
                            )}
                          </div>
                        </div>
                      </div>

                      {certificate.expiryDate ? (
                        <div className="flex items-start gap-3">
                          <Calendar className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              {t("certificate.certificateDisplay.expiryDate")}
                            </div>
                            <div className="text-sm text-gray-900">
                              {format(
                                new Date(certificate.expiryDate),
                                "yyyy-MM-dd"
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <Shield className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              {t("certificate.certificateDisplay.validity")}
                            </div>
                            <div className="text-sm text-gray-900">
                              {t("certificate.certificateDisplay.neverExpires")}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getStatusBadgeClass(
                          certificate
                        )}`}
                      >
                        {getStatusText(certificate)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(certificate.id)}
                      disabled={downloading === certificate.id}
                      className="main-dark-button"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {downloading === certificate.id
                        ? t("certificate.certificateDisplay.downloading")
                        : t("certificate.certificateDisplay.download")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const verifyUrl = `/certificate/verify/${certificate.verificationCode}`;
                        window.open(verifyUrl, "_blank");
                      }}
                      className="text-gray-700 hover:bg-gray-50 rounded"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t("certificate.certificateDisplay.verify")}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* PDF Preview */}
              {loadingPreview[certificate.id] ? (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center text-gray-500">
                      <p className="text-sm">
                        {t(
                          "certificate.certificateDisplay.loadingCertificatePreview"
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ) : previewUrls[certificate.id] ? (
                <div className="bg-white rounded-lg">
                  <PdfPreview
                    fileUrl={previewUrls[certificate.id]}
                    fileName={`${
                      certificate.certificateTemplate?.name || "Certificate"
                    } - ${certificate.certificateNumber}.pdf`}
                    hidePreviewButton={true}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      )}
    </>
  );
};

export default CertificateDisplay;
